/**
 * dashboard/charts.tsx — the small set of hand-rolled charts for the Manager Metrics Dashboard.
 *
 * WHY HAND-ROLLED, AND WHY THESE ARE NOT "REAL" CHARTS. Russell asked the dashboard to be "WAY simpler
 * and just have some charts". A charting library (Recharts &c.) pulls ~100kB of D3 to draw a bar, which
 * is the opposite of simpler; these are ~two dozen lines of flexbox + one <rect> each, themed straight
 * off the design tokens. They are deliberately SUPPLEMENTS that sit ABOVE the existing tables/cards —
 * the table is the chart's text equivalent (MD-NFR07: no colour-only charts), so nothing here replaces
 * a figure a reader could otherwise read. "Simpler" means a picture first, with the exact numbers still
 * one glance below.
 *
 * THE ONE RULE THAT MATTERS FOR PRIVACY. A cohort-suppressed value is `null`, and a null row draws NO
 * BAR — it shows the muted "Too few" the rest of the dashboard uses. A zero-length bar would read as
 * "0 people", which is precisely the withheld figure the tables take such care not to hand back; the
 * chart must never be the surface that leaks it. `BarChart` therefore takes `number | null` and treats
 * the two utterly differently, and the max it scales bars against is the max of the VISIBLE values, not
 * a population total (a total would let a reader subtract the drawn bars to recover a suppressed one,
 * the same subtraction the Overview/Learning prose warns against).
 */
import type { ReactNode } from 'react';

/** One bar. `value: null` means cohort-suppressed — drawn as "Too few", never as a zero bar. */
export interface BarDatum {
  label: string;
  /** The measured value, or null when it was suppressed below the cohort floor. */
  value: number | null;
  /** Optional link for the label (e.g. a tool detail page). Labels are never people. */
  href?: string;
}

/**
 * What a bar row DRAWS. Pulled out of the JSX as a pure function because packages/web has no jsdom, so
 * the component itself cannot be rendered in a test — this is the way the one privacy-critical decision
 * (a suppressed value draws NO bar, not a zero bar) is made testable, the same shape as
 * home/strips.ts's extracted decisions.
 *
 * `bar` is null exactly when the value is suppressed OR there is nothing to scale against. A rendered
 * row keys off `bar == null` to decide between a fill and the "Too few" tick, so a suppressed row can
 * never acquire a width — and a genuine zero scales to the 1.5% floor only when there is a positive max
 * to sit under, so it reads as "present but nil" rather than borrowing a suppressed row's blankness.
 */
export function barGeometry(
  value: number | null,
  max: number,
): { suppressed: boolean; bar: { widthPct: number } | null } {
  if (value == null) return { suppressed: true, bar: null };
  if (max <= 0 || value <= 0) return { suppressed: false, bar: { widthPct: 0 } };
  // Floor a POSITIVE bar to 1.5% so a real-but-tiny value stays visible; a genuine 0 stays 0 above,
  // so "nobody" never borrows a small-positive width.
  return { suppressed: false, bar: { widthPct: Math.max((value / max) * 100, 1.5) } };
}

/** The largest VISIBLE (non-suppressed) value in a series — never a total, so bars cannot be subtracted. */
export function visibleMax(data: BarDatum[]): number {
  return data.reduce((m, d) => (d.value == null ? m : Math.max(m, d.value)), 0);
}

/**
 * A horizontal bar chart for a labelled series. Purely presentational: the caller has already decided
 * which values are suppressed (passing null) and in what order the rows appear — this component never
 * re-sorts, because on the Overview the row order is load-bearing (sorting would order the suppressed
 * rows and reveal which withheld figure is larger).
 *
 * The whole figure is one `role="img"` with an `aria-label` summary; the on-page table beneath it is
 * the full text equivalent, so a screen reader is never asked to parse bar geometry.
 */
export function BarChart({
  data,
  unit,
  caption,
  formatValue = (n) => n.toLocaleString(),
}: {
  data: BarDatum[];
  /** What one unit of the bar IS, e.g. "people" — used only in the aria summary. */
  unit: string;
  /** Optional caption rendered beneath the chart (muted), for the "not the period" disclaimers. */
  caption?: ReactNode;
  formatValue?: (n: number) => string;
}) {
  // Scale to the largest VISIBLE value, never to a total (see the file header + visibleMax).
  const max = visibleMax(data);
  const anyVisible = data.some((d) => d.value != null);
  const summary = anyVisible
    ? `Bar chart of ${unit} by ${data.length === 1 ? 'one row' : `${data.length} rows`}.`
    : `No ${unit} to chart.`;

  return (
    <figure className="tb-chart" role="img" aria-label={summary}>
      <div className="tb-chart-rows">
        {data.map((d) => {
          const { suppressed, bar } = barGeometry(d.value, max);
          return (
            <div className="tb-chart-row" key={d.label}>
              <div className="tb-chart-label">
                {d.href ? <a href={d.href}>{d.label}</a> : d.label}
              </div>
              <div className="tb-chart-track">
                {/* A fill exists iff barGeometry gave one — suppressed rows never do (file header). */}
                {bar != null && (
                  <div className="tb-chart-fill" style={{ width: `${bar.widthPct}%` }} />
                )}
              </div>
              <div className={`tb-chart-value${suppressed ? ' tb-chart-value-suppressed' : ''}`}>
                {suppressed ? 'Too few' : formatValue(d.value as number)}
              </div>
            </div>
          );
        })}
      </div>
      {caption != null && <figcaption className="tb-muted">{caption}</figcaption>}
    </figure>
  );
}

/** One point of a LineChart. `value: null` means cohort-suppressed — the line BREAKS, never plots a 0. */
export interface LineDatum {
  /** The day, `YYYY-MM-DD` (UTC). */
  date: string;
  /** Distinct active people that day, or null when suppressed below the cohort floor. */
  value: number | null;
}

/**
 * The largest VISIBLE (non-suppressed) value across the series. Same rule as `visibleMax` for bars: it
 * scales the line against what is drawn, NEVER a population total, so no reader can subtract plotted
 * points to recover a suppressed one.
 */
export function seriesVisibleMax(points: LineDatum[]): number {
  return points.reduce((m, p) => (p.value == null ? m : Math.max(m, p.value)), 0);
}

/**
 * Project each point to an `{x, y}` in the plot box, or `null` when it was suppressed. Extracted as a
 * pure function for the same reason as `barGeometry`: packages/web has no jsdom, so this — where the two
 * privacy-critical decisions live — is what a test can assert on:
 *   - a suppressed point (value null) projects to `null`: it becomes a GAP, never a plotted coordinate;
 *   - a GENUINE 0 projects to the baseline (`y = height`) and IS plotted — "present but nil", never a
 *     gap, so a real quiet day reads differently from a withheld one.
 * x is spread evenly by index (one calendar day per slot); a single point sits centred.
 */
export function lineCoords(
  points: LineDatum[],
  opts: { width: number; height: number; max: number },
): Array<{ x: number; y: number } | null> {
  const { width, height, max } = opts;
  const n = points.length;
  return points.map((p, i) => {
    if (p.value == null) return null;
    const x = n <= 1 ? width / 2 : (i / (n - 1)) * width;
    const y = max <= 0 ? height : height - (p.value / max) * height;
    return { x, y };
  });
}

/**
 * Group consecutive plotted coordinates into runs. A `null` (suppressed day) ends the current run and
 * starts a new one AFTER it — that gap is how the line breaks across a withheld day rather than sloping
 * through it as if the count were known. A run of one is kept (rendered as a lone dot), so an isolated
 * plotted day between two suppressed ones is still visible.
 */
export function coordRuns(
  coords: Array<{ x: number; y: number } | null>,
): { x: number; y: number }[][] {
  const runs: { x: number; y: number }[][] = [];
  let cur: { x: number; y: number }[] = [];
  for (const c of coords) {
    if (c == null) {
      if (cur.length) runs.push(cur);
      cur = [];
    } else {
      cur.push(c);
    }
  }
  if (cur.length) runs.push(cur);
  return runs;
}

/** `YYYY-MM-DD` → a short `D Mon` axis label, in UTC so it matches the day the server bucketed by. */
function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}`;
}

/**
 * A line chart of a daily people-count series (the Overview active-people graph). Hand-rolled SVG in the
 * same spirit as BarChart, and it obeys the same one rule that matters: a suppressed day is a GAP in the
 * line (see `coordRuns`/`lineCoords`), never a zero plotted on the axis.
 *
 * MD-NFR07 (no colour-only charts): unlike BarChart this one has NO table beneath it — the three cards it
 * replaces are gone. Its text equivalent is therefore carried by the `aria-label`, which states the span,
 * the endpoints and the peak in words, and by the peak/latest figures printed beside the graph. The line
 * itself adds no meaning a non-sighted reader misses. It scales to the max VISIBLE value, never a total.
 */
export function LineChart({
  data,
  unit,
  caption,
  formatValue = (n) => n.toLocaleString(),
}: {
  data: LineDatum[];
  /** What one unit of the y-axis IS, e.g. "people" — used in the aria summary. */
  unit: string;
  caption?: ReactNode;
  formatValue?: (n: number) => string;
}) {
  // A fixed viewBox scaled responsively by CSS (width:100%); coordinates are in these user units.
  const W = 640;
  const H = 160;
  const max = seriesVisibleMax(data);
  const coords = lineCoords(data, { width: W, height: H, max });
  const runs = coordRuns(coords);
  const visible = data.filter((p) => p.value != null) as { date: string; value: number }[];
  const suppressedDays = data.length - visible.length;

  // The aria summary IS the text equivalent (there is no table). State span, endpoints and peak in words.
  const first = visible[0];
  const last = visible[visible.length - 1];
  let summary: string;
  if (first == null || last == null) {
    summary = `No ${unit} to chart over ${data.length} days.`;
  } else {
    const peak = visible.reduce((a, b) => (b.value > a.value ? b : a), first);
    summary =
      `${unit} per day over ${data.length} days, ${shortDate(first.date)} to ${shortDate(last.date)}: ` +
      `starts at ${formatValue(first.value)}, ends at ${formatValue(last.value)}, ` +
      `peaks at ${formatValue(peak.value)} on ${shortDate(peak.date)}` +
      (suppressedDays > 0 ? `; ${suppressedDays} day(s) below the reporting floor` : '') +
      '.';
  }

  // Up to four evenly-spaced date ticks, so a 180-day window is not a wall of labels.
  const tickCount = Math.min(4, data.length);
  const tickIdx =
    data.length <= 1
      ? [0]
      : Array.from({ length: tickCount }, (_, k) =>
          Math.round((k / (tickCount - 1)) * (data.length - 1)),
        );

  return (
    <figure className="tb-chart tb-chart-line" role="img" aria-label={summary}>
      {visible.length === 0 ? (
        <p className="tb-muted tb-chart-line-empty">No activity to chart for this period yet.</p>
      ) : (
        <>
          <svg
            className="tb-line-svg"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {/* baseline */}
            <line x1="0" y1={H} x2={W} y2={H} className="tb-line-axis" />
            {runs.map((run, ri) => (
              <polyline
                key={ri}
                className="tb-line-stroke"
                points={run.map((c) => `${c.x},${c.y}`).join(' ')}
              />
            ))}
            {coords.map((c, i) =>
              c == null ? null : <circle key={i} className="tb-line-dot" cx={c.x} cy={c.y} r={3} />,
            )}
          </svg>
          <div className="tb-line-xaxis" aria-hidden="true">
            {tickIdx.map((i) => (
              <span key={i}>{shortDate(data[i]?.date ?? '')}</span>
            ))}
          </div>
        </>
      )}
      {caption != null && <figcaption className="tb-muted">{caption}</figcaption>}
    </figure>
  );
}

/** One segment of a SegmentedBar. `count` is a real, non-suppressed count (the caller checks first). */
export interface Segment {
  label: string;
  count: number;
  /** A design token, e.g. 'var(--accent)'. */
  color: string;
}

/**
 * A single full-width bar split into named segments that PARTITION a whole (training completion:
 * complete / in progress / not started). Unlike BarChart this one does span a total — but only a total
 * that is already published on the same card (the `people` count), and only because the three states
 * are a partition the reader can already see. Anything that is a SUBSET rather than a partition
 * (overdue spans in-progress and not-started) must be shown as a called-out number BELOW, never as a
 * fourth segment, or the bar would exceed the population.
 */
export function SegmentedBar({
  segments,
  total,
  legendNote,
}: {
  segments: Segment[];
  /** The population the segments partition; segments should sum to this. */
  total: number;
  legendNote?: ReactNode;
}) {
  const summary = segments.map((s) => `${s.label} ${s.count}`).join(', ');
  return (
    <figure className="tb-chart" role="img" aria-label={`Of ${total}: ${summary}.`}>
      <div className="tb-seg-track">
        {segments.map((s) =>
          s.count > 0 ? (
            <div
              className="tb-seg-fill"
              key={s.label}
              style={{ width: `${(s.count / total) * 100}%`, background: s.color }}
            />
          ) : null,
        )}
      </div>
      <ul className="tb-seg-legend">
        {segments.map((s) => (
          <li key={s.label}>
            <span className="tb-seg-swatch" style={{ background: s.color }} aria-hidden="true" />
            {s.label} — {s.count.toLocaleString()}
          </li>
        ))}
      </ul>
      {legendNote != null && <figcaption className="tb-muted">{legendNote}</figcaption>}
    </figure>
  );
}
